//BlueHandsMy.jsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import MainLayout from '../../layouts/MainLayout';
import logo from '../../assets/logo.svg';

const BlueHandsMy = () => {
  const authState = useSelector(state => state.authReducer);
  const navigate = useNavigate();
  const [userId, setUserId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  const [userCode, setUserCode] = useState(authState.user.think5whyscode);
  const [bluehands, setBlueHands] = useState([]);
  const [fetchData, { loading }] = useFetch();
  const [selectedBlueHandId, setSelectedBlueHandId] = useState(null);

  const roleList = [
    'Aggregator',
    'Master Aggregator',
    'Contractor',
    'Sub Contractor',
    'Principal Employer',
    'Verifier',
    'Admin',
    'Super Admin',
    'Referrer',
  ];

  const [myRole, setMyRole] = useState(roleList[0]); 

  const fetchBlueHands = useCallback(() => {
    const config = { 
        url: "/blueprofile/myall", 
        method: "post", 
        data: {userId: userId, userCode: userCode, myRole: myRole},
        headers: { Authorization: authState.token } 
      };
      // console.log("BlueHand Config: ",config)
      fetchData(config, { showSuccessToast: false })
      .then((response) => {
        setBlueHands(response?.allBlueProfile || []);  
      })
      .catch(error => console.error("Error fetching data:", error));
  }, [authState.token, fetchData, myRole]);

    useEffect(() => {
      if (!authState.isLoggedIn) return;
      fetchBlueHands();
    }, [authState.isLoggedIn, fetchBlueHands]);

  
  const handleView = (bluehandId) => {
    setSelectedBlueHandId(prevId => (prevId === bluehandId ? null : bluehandId));
  };

  const formatDateTime = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

  const sortedBlueHands = [...bluehands].sort((a, b) => b.bluehandTitle - a.bluehandTitle);

  return (
    <>
      <MainLayout>
        <div className='flex justify-center mt-12'>
          <h2 className='text-lg text-white'>My Blue Hands</h2>
        </div>
        <div className='flex flex-row justify-center mt-4 border border-yellow-300 py-4 px-4'>
          <p className="text-white mr-4 p-3" style={{ height: "48px"}}>Select Role:</p>
          <select
            id="myrole"
            value={myRole}
            onChange={(e) => {
              setMyRole(e.target.value);
            }}
            className="p-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ height: "48px", width: "250px" }}
          >
            {roleList.map((role) => (
              <option key={role} value={role}>{role}</option>
            ))}
          </select>

          <button
            onClick={fetchBlueHands}
            className="bg-blue-500 text-white ml-8 px-6 py-3 hover:bg-blue-600"
            style={{ width: "150px" }}
          >
            Get Profiles
          </button>
        </div>
        <div className="flex justify-center mt-2 text-yellow-200">My BlueHand Profiles As {myRole}</div>

        <div >
            <div className="border border-gray-300 p-4 mt-2">
                <div className="mt-2">
                  {bluehands && bluehands.length !== 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <h3 className='text-m mt-1 mx-1 text-yellow-200'>{bluehands.length} Blue Hands Found!</h3>
                    </div>
                  )}
                </div>
              {loading ? (
                <Loader />
              ) : (
                <div>
                  {bluehands && bluehands.length === 0 ? (
                    <div className='h-[300px] flex items-center justify-center gap-4 text-yellow-300'>
                      <div className="flex justify-between items-center">
                        <h3 className='text-lg mt-1 mx-2 text-orange-400'>No BlueHand Record Found!</h3>
                   </div>
                    </div>
                  ) : (
                    sortedBlueHands.map((bluehand, index) => (
                      <div key={bluehand._id} className='bg-gray-500 my-1 p-1 text-white rounded-md shadow-md text-m'>
                        <div className='flex'>
                          <span className='mr-2'>{index + 1}.</span> {bluehand.name}
                          <Tooltip text={"View details"} position={"top"}>
                            <span className='ml-auto mr-4 text-blue-500 cursor-pointer' onClick={() => handleView(bluehand._id)}>
                              <i className="fa-solid fa-eye"></i>
                            </span>
                          </Tooltip>
                          <Tooltip text={"Edit this record"} position={"top"}>
                            <Link to={`/bluehands-bluehand/${bluehand._id}`} className='mr-4 text-green-600 cursor-pointer'>
                              <i className="fa-solid fa-pen"></i>
                            </Link>
                          </Tooltip>
                        </div>
                        {selectedBlueHandId === bluehand._id && (
                          <div className='bg-gray-600 text-white mt-2 px-4'>
                            <p className='text-yellow-200 mt-2'>{bluehand.name}</p>
                            <p className='mt-2'><strong>Gender:</strong>{bluehand.gender}</p>
                            <p className='mt-2'><strong>Email:</strong>{bluehand.email}</p>
                            <p className='mt-2'><strong>Date Posted:</strong> {formatDateTime(bluehand.dateCreated)}</p>
                            <p className='mt-2'><strong>Active Status:</strong>{bluehand.isActive ? "Active" : "Inactive"}</p>
                            <p className='mt-2'><strong>Is Available:</strong>{bluehand.isAvailable ? "Yes" : "No"}</p>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
      </MainLayout>
    </>
  )
}

export default BlueHandsMy